import { Component } from 'react';
import { inject, observer } from 'mobx-react';
import { Row, Col, Modal, OverlayTrigger, Tooltip } from 'react-bootstrap';

//components
import withLocalization from '~/hoc/withLocalization';
import withRemoveDialog from '~/hoc/withRemoveDialog';
import SwitchWidget from '../../SwitchWidget.js';
import TableWidget from '../../TableWidget.js';
import AddEditOvertimeSetting from './addEditOvertimeSettings.js';
import Button from '~/components/CustomButton/CustomButton';
import WagetypeRefetch from '../../WagetypeRefetch.js';

//elements
import LoadingSpinner from '~/elements/LoadingSpinner';
import WageTypeDisplay from '~/elements/WageTypeDisplay.js';

//constants
import { WAGE_TYPE_HOURLY_FALLBACK_NOTE } from '~/utils/constants';

//assets
import CloseModalIcon from '~/assets/img/CloseApproveModalIcon.svg';

const formatData = function (data, t) {
    return data.map(item => ({
        ...item,
        wageTypeDisplay: <WageTypeDisplay wageType={item.integrated_wagetype} hourlyWageType={item.hr_int_wagetype} t={t} />,
    }));
}

class OvertimeSetting extends Component {
    constructor(props) {
        super(props);
        this.state = {
            overtimeSettingList: [],
            isEdit: false,
            editData: {},
            wageType: props.commonStore.getWageTypesAccordingToIntegrations(props.commonStore?.config?.integration_details, 'at') || [],
            lastUpdated: props.commonStore.getWageTypesAccordingToIntegrations(props.commonStore?.config?.integration_details, 'last_updated') || null,
            isLoadingWageTypes: false,
            showAllWageTypes: false,
            loading: false,
            requireCommentForOvertime: false,
            requireCommentForMandatoryOrUrgentWorkOvertime: false
        }
        this.updateData = this.updateData.bind(this);
        this.deleteOvertime = this.deleteOvertime.bind(this);
    }

    updateData = async (data) => {
        const { commonStore, t, clientStore } = this.props;
        if (data.hasError)
            return commonStore.addNotification(t(data.errors[0].error), null, 'warning');

        this.setState({ loading: true }, async () => {
            try {
                if (this.state?.editData?.id)
                    await clientStore.editOvertimeSetting(this.state.editData.id, data);
                else
                    await clientStore.saveOvertimeSetting(data);
                commonStore.addNotification(t('Overtime setting saved successfully!'), null, 'success');
                this.loadData()
            } catch (err) {
                console.error(err)
                this.setState({ loading: false })
            }
        })
    }

    componentDidMount() {
        this.setState({ loading: true }, this.loadData);
    }

    loadData = async () => {
        const { clientStore } = this.props;
        try {
            const result = await clientStore.getOvertimeSetting();
            const basicRules = clientStore.currentEntity?.client?.data?.basicRules || {};
            this.setState({
                overtimeSettingList: result.overtimeSettingList,
                requireCommentForOvertime: basicRules.requireCommentForOvertime ? basicRules.requireCommentForOvertime : false,
                requireCommentForMandatoryOrUrgentWorkOvertime: basicRules.requireCommentForMandatoryOrUrgentWorkOvertime ? basicRules.requireCommentForMandatoryOrUrgentWorkOvertime : false,
                isEdit: false,
                loading: false,
            });
        } catch (err) {
            console.error(err);
            this.setState({ loading: false });
        }
    }

    deleteOvertime = async (id) => {
        const { clientStore, commonStore, t } = this.props;
        try {
            await clientStore.deleteOvertimeSetting(id, false);
            commonStore.addNotification(t('Overtime setting deleted successfully!'), null, 'success');
            this.loadData()
        } catch (err) {
            console.error(err);
            this.setState({ loading: false });
        }
    }

    handleDelete = (index) => {
        const data = this.state.overtimeSettingList[index];
        const id = data?.id

        if (!id) return;
        this.setState({ loading: true }, () => {
            return this.props.handleDelete(id, (id) => {
                return this.deleteOvertime(id);
            }, null, () => this.setState({ loading: false }));
        })
    }

    editOvertime = (index) => {
        const data = this.state.overtimeSettingList[index];
        this.toggleModal(true, data);
    }

    toggleModal = (value, data) => {
        this.setState({ isEdit: value, editData: data });
    }

    handleSwitch = (name, evt) => {
        this.props.handleUserDataChange(name, evt);
        if (name === 'requireCommentForOvertime' && !evt) {
            this.props.handleUserDataChange('requireCommentForMandatoryOrUrgentWorkOvertime', false);
            return this.setState({ requireCommentForOvertime: false, requireCommentForMandatoryOrUrgentWorkOvertime: false });
        }
        this.setState({ [name]: evt });
    }

    refetchWageType = async () => {
        const { commonStore, t } = this.props;
        try {
            await commonStore.refetchWagetypes();
            commonStore.addNotification(t('Successfully refetched wagetypes'), null, 'success');
            const _wagetype = commonStore.getWageTypesAccordingToIntegrations(commonStore?.config?.integration_details, 'at') || [];
            const _lastUpdated = commonStore.getWageTypesAccordingToIntegrations(commonStore?.config?.integration_details, 'last_updated') || null;
            this.setState({ wageType: _wagetype, lastUpdated: _lastUpdated, isLoadingWageTypes: false });

        } catch (err) {
            console.error(err);
            commonStore.addNotification(t('Something went wrong'), null, 'error');
            this.setState({ isLoadingWageTypes: false });
        }
    }

    render() {
        const { t } = this.props;
        const { isEdit, loading, wageType, lastUpdated, isLoadingWageTypes, showAllWageTypes,
            requireCommentForOvertime, requireCommentForMandatoryOrUrgentWorkOvertime } = this.state
        const effectiveWageType = showAllWageTypes ? this.props.commonStore.getAllWageTypesCombined() : wageType;
        const clientBasicRules = this.props.clientStore.currentEntity?.client?.data?.basicRules || {};
        const canIncludeMandatoryOrUrgentWork = !!(clientBasicRules.allowManagingTimelogsManually
            || clientBasicRules.urgentWorkSettings?.allowUrgentWork);

        if (loading) return <LoadingSpinner />

        return (
            <>
                <Row className="mt-2">
                    <Col sm={6}>
                        <Row>
                            <Col sm={2} className="mt-1">
                                <SwitchWidget
                                    value={requireCommentForOvertime}
                                    onChange={checked => this.handleSwitch('requireCommentForOvertime', checked)} />
                            </Col>
                            <Col sm={10}>
                                <label className="control-label">{t('Require comment for overtime hours')}</label>
                                <OverlayTrigger
                                    placement="right"
                                    overlay={
                                        <Tooltip id="tooltip-top">
                                            {t("When enabled, a comment must be added to the timesheet if it contains overtime hours.")}
                                        </Tooltip>
                                    }
                                >
                                    <i className="fa fa-info-circle text-muted ms-1" style={{ opacity: '0.5' }} aria-hidden="true"></i>
                                </OverlayTrigger>
                            </Col>
                        </Row>
                    </Col>
                    {canIncludeMandatoryOrUrgentWork &&
                        <Col sm={6}>
                            <Row>
                                <Col sm={2} className="mt-1">
                                    <SwitchWidget
                                        value={requireCommentForMandatoryOrUrgentWorkOvertime}
                                        disabled={!requireCommentForOvertime}
                                        onChange={checked => this.handleSwitch('requireCommentForMandatoryOrUrgentWorkOvertime', checked)} />
                                </Col>
                                <Col sm={10}>
                                    <label className="control-label">{t('Include Mandatory / Urgent Work overtime')}</label>
                                    <OverlayTrigger
                                        placement="left"
                                        overlay={
                                            <Tooltip id="tooltip-top">
                                                {t("When enabled, the comment is also required on timesheets registered as mandatory overtime or urgent work.")}
                                            </Tooltip>
                                        }
                                    >
                                        <i className="fa fa-info-circle text-muted" style={{ opacity: '0.5' }} aria-hidden="true"></i>
                                    </OverlayTrigger>
                                </Col>
                            </Row>
                        </Col>
                    }
                </Row>
                <Row>
                    <Col xs={{ span: 4, offset: 8 }} className='d-flex justify-content-end mt-2'>
                        <Button wd fill onClick={() => this.toggleModal(true, {})}>
                            <i className="fa fa-plus-circle fa-xl margin-right-10" /> {this.props.t('Add new')}
                        </Button>
                    </Col>
                </Row>
                {!!wageType.length &&
                    <WagetypeRefetch
                        t={this.props.t}
                        refetch={() => this.setState({ isLoadingWageTypes: true }, this.refetchWageType)}
                        lastUpdated={lastUpdated}
                        isLoading={isLoadingWageTypes}
                        dateTimeRules={this.props.commonStore.getDateTimeRules()}
                        extraNote={t(WAGE_TYPE_HOURLY_FALLBACK_NOTE)}
                        showAllWageTypes={showAllWageTypes}
                        onToggleShowAllWageTypes={(value) => this.setState({ showAllWageTypes: value })}
                    />
                }
                <TableWidget
                    headers={['Name', 'Overtime', 'Wage Type', 'Ratio']}
                    metaData={[
                        { field: 'name', type: 'text' },
                        { field: 'overtime', type: 'number' },
                        { field: 'wageTypeDisplay', html: true },
                        { field: 'ratio', type: 'text' }
                    ]}
                    data={formatData(this.state.overtimeSettingList, t)}
                    onChange={_ => { }}
                    minRemoveIndex={0}
                    customDeleteHandler={this.handleDelete}
                    page={'overtimeSettings'}
                    customFormHandler={this.editOvertime}
                />
                <Modal
                    className={"modal right fade modal_revised"}
                    backdrop="static"
                    show={isEdit}
                    onHide={() => this.toggleModal(false, {})}
                >
                    <Modal.Header>
                        <Modal.Title className="w-100 text-center m-0">{t('Overtime Settings')}</Modal.Title>
                        <img
                            src={CloseModalIcon}
                            className="cursor-pointer"
                            alt='close_button'
                            onClick={() => this.toggleModal(false, {})}
                        />
                    </Modal.Header>
                    <Modal.Body className='position-relative'>
                        <AddEditOvertimeSetting
                            values={this.state.editData}
                            updateData={this.updateData}
                            cancel={() => this.toggleModal(false, {})}
                            payroll_wagetypes={effectiveWageType}
                            integrationType={this.props.commonStore?.config?.integration_details?.product}
                            commonStore={this.props.commonStore}
                            t={this.props.t}
                        />
                    </Modal.Body>
                </Modal>
            </>
        );
    }
}
export default inject('clientStore', 'commonStore', 'userStore')(withLocalization(withRemoveDialog(observer(OvertimeSetting))));
